import {motion} from 'framer-motion';
import {Image} from '@shopify/hydrogen';
import {useRef, useEffect} from 'react';
import {useInView} from 'react-intersection-observer';
import Lottie from 'lottie-web';

import type {SanityModuleScribbleBanner} from '../../types';

import {pathVars} from '../ui/animationVars';

export default function ScribbleBannerModule({
  module,
}: {
  module: SanityModuleScribbleBanner;
}) {
  const {heading, text, image, lottie, lottieFile}: any = module;

  const {ref, inView} = useInView({
    threshold: 0.7,
    triggerOnce: false,
  });

  const lottieRef = useRef<any>();

  useEffect(() => {
    const anim = Lottie.loadAnimation({
      container: lottieRef.current, // the dom element that will contain the animation
      renderer: 'svg',
      loop: true,
      autoplay: true,
      path: lottieFile?.asset?.url, // the path to the animation json
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
      },
    });

    anim.play();

    return () => {
      anim.destroy();
    };
  }, []);

  return (
    <div className="relative h-screen">
      {lottie ? (
        <div
          ref={lottieRef}
          className="absolute top-0 left-0 h-full w-full object-cover"
        />
      ) : (
        <Image
          loading="lazy"
          data={image}
          alt={heading}
          width="inherit"
          height="inherit"
          className="absolute top-0 left-0 h-full w-full object-cover"
        />
      )}
      <div
        ref={ref}
        className="relative z-10 w-full px-gutter pt-space-m text-center"
      >
        <h1 className="blockH3 font-bold text-orange">{heading}</h1>
        <h1 className="blockH3 mt-4 block">
          {text?.prefix}
          <div className="relative inline-block w-max">
            <span className="relative z-10 font-bold">{text?.underlined}</span>
            <svg
              className="absolute -top-full left-0 w-full scale-105 md:-top-1/4"
              width="284"
              height="114"
              viewBox="0 0 284 114"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <motion.path
                variants={pathVars}
                initial="initial"
                animate={inView ? 'animate' : 'initial'}
                d="M244.296 22.682C193.021 -1.96536 117.179 -1.22629 57.902 10.228C43.1757 13.0737 -18.7858 37.1564 10.5135 67.5846C24.5027 82.1127 39.8421 84.3879 57.6842 90.5663C98.6185 104.741 141.946 111.391 185.25 110.673C211.352 110.24 242.699 106.276 265.794 89.992C272.898 84.9832 280.531 76.8155 281.062 66.8378C281.181 23.027 239.778 12.7391 234.871 4.79595"
                stroke="#F27021"
                strokeWidth="5"
                strokeLinecap="round"
              />
            </svg>
          </div>
          {text?.suffix}
        </h1>
      </div>
    </div>
  );
}
